import React from 'react';
import styled from 'styled-components';

import { Button, Modal } from './index';
import { InvoiceComparisonResultDto } from '../service-proxies';
import { formatPrice } from '../_utils/prices';

interface IProcessingCompleteModalProps {
  onCancel: () => void;
  onSaveAndProceed: () => void;
  isOpen: boolean;
  comparisonResult?: InvoiceComparisonResultDto | null;
  isSaving?: boolean;
}


const ContentContainer = styled.div`
  padding: 20px 0;
`;

const HeaderInfo = styled.div`
  text-align: center;
  margin-bottom: 25px;
`;

const Subtitle = styled.p`
  font-size: 1em;
  margin-bottom: 0;
  line-height: 1.4;
`;

const ErrorText = styled.span`
  color: #d32f2f;
  font-weight: bold;
`;

const TableContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 25px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9em;
`;

const TableHeader = styled.thead`
  background-color: #f5f5f5;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const TableHeaderCell = styled.th`
  padding: 12px 8px;
  text-align: left;
  font-weight: bold;
  border-bottom: 2px solid #ddd;
  font-size: 0.85em;
  color: #666;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f9f9f9;
  }
  
  &:hover {
    background-color: #f0f0f0;
  }
`;

const TableCell = styled.td`
  padding: 10px 8px;
  border-bottom: 1px solid #eee;
  font-size: 0.85em;
`;


const ProcessingCompleteModal: React.FC<IProcessingCompleteModalProps> = ({
  onCancel,
  onSaveAndProceed,
  isOpen,
  comparisonResult,
  isSaving = false
}) => {

  // Debug logging
  console.log('=== ProcessingCompleteModal Debug ===');
  console.log('isOpen:', isOpen);
  console.log('comparisonResult:', comparisonResult);
  console.log('comparisonResult?.itemsWithDiscrepancies:', comparisonResult?.itemsWithDiscrepancies);
  console.log('itemsWithDiscrepancies length:', comparisonResult?.itemsWithDiscrepancies?.length);
  
  // Use API data from the comparison result
  const totalItems = comparisonResult?.totalItemsCompared ?? 0; // Use totalItemsCompared for display
  const itemsWithPricingErrors = comparisonResult?.itemsWithPricingErrors ?? 0;
  const itemsPartiallyShipped = comparisonResult?.itemsPartiallyShipped ?? 0;
  const itemsWithMissingEdiData = comparisonResult?.itemsWithMissingEdiData ?? 0;

  // Calculate percentage for display
  const errorPercentage = totalItems > 0 ? Math.round((itemsWithPricingErrors / totalItems) * 100) : 0;
  const partiallyShippedPercentage = totalItems > 0 ? Math.round((itemsPartiallyShipped / totalItems) * 100) : 0;
  const missingEdiPercentage = totalItems > 0 ? Math.round((itemsWithMissingEdiData / totalItems) * 100) : 0;
  
  // Transform API data to table format - showing items with discrepancies or missing Vendor data
  const tableData = comparisonResult?.itemsWithDiscrepancies
    ?.filter(item => 
      item.hasPriceDiscrepancy || 
      item.hasQuantityDiscrepancy || 
      item.hasTotalDiscrepancy || 
      item.hasTaxDiscrepancy ||
      item.ediDataMissing
    )
    .map(item => ({
      dateRange: item.invoiceCreationDate ? new Date(item.invoiceCreationDate).toLocaleDateString() : 'N/A',
      ottoPO: item.invoiceNumber || 'N/A', // Using invoice number as PO reference
      invoice: item.ediInvoiceNumber || 'N/A',
      productSku: item.drs4DrsSku || 'N/A',
      vendorName: item.vendorName || 'N/A',
      ottoQty: item.drs4DrsQuantity != null ? item.drs4DrsQuantity.toString() : 'N/A',
      vendorQty: item.ediDataMissing || item.ediQuantity == null ? 'Not Available' : item.ediQuantity.toString(),
      ottoPrice: item.drs4DrsPrice != null ? formatPrice(item.drs4DrsPrice) : 'N/A',
      vendorPrice: item.ediDataMissing || !item.ediPrice ? 'Not Available' : formatPrice(parseFloat(item.ediPrice)),
      hasDiscrepancy: item.hasPriceDiscrepancy || item.hasQuantityDiscrepancy || item.hasTotalDiscrepancy || item.hasTaxDiscrepancy,
      ediDataMissing: item.ediDataMissing
    })) || [];

  return (
    <Modal
      isOpen={isOpen}
      options={{
        title: 'Processing Complete!',
        noBackdrop: true,
      }}
      onDecline={onCancel}
      onConfirm={() => {}} // Actions are handled with custom buttons, this is a passthrough
      width={'80%' as unknown as number}
      preloader={false}
    >
      <Modal.Body>
        <ContentContainer>
          <HeaderInfo>
            <Subtitle>
              {itemsWithPricingErrors > 0 || itemsWithMissingEdiData > 0 ? (
                <>
                  {itemsWithPricingErrors > 0 && (
                    <ErrorText>OTTO found {itemsWithPricingErrors} out of {totalItems} products ({errorPercentage}%) with pricing errors</ErrorText>
                  )}
                  {itemsWithPricingErrors > 0 && itemsWithMissingEdiData > 0 && <br />}
                  {itemsWithMissingEdiData > 0 && (
                    <span style={{ color: '#ff9800', fontWeight: 'bold' }}>
                      {itemsWithMissingEdiData} out of {totalItems} products ({missingEdiPercentage}%) have no Vendor data available for comparison
                    </span>
                  )}
                  {itemsPartiallyShipped > 0 && (itemsWithPricingErrors > 0 || itemsWithMissingEdiData > 0) && <br />}
                  {itemsPartiallyShipped > 0 && (
                    <span style={{ color: '#2196f3', fontWeight: 'bold' }}>
                      {itemsPartiallyShipped} out of {totalItems} products ({partiallyShippedPercentage}%) are partially shipped (quantity discrepancies only)
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span style={{ color: '#4caf50', fontWeight: 'bold' }}>
                    All {totalItems} products verified successfully - no pricing errors found!
                  </span>
                  {itemsPartiallyShipped > 0 && (
                    <>
                      <br />
                      <span style={{ color: '#2196f3', fontWeight: 'bold' }}>
                        {itemsPartiallyShipped} out of {totalItems} products ({partiallyShippedPercentage}%) are partially shipped (quantity discrepancies only)
                      </span>
                    </>
                  )}
                </>
              )}
            </Subtitle>
          </HeaderInfo>

          <TableContainer>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Date</TableHeaderCell>
                  <TableHeaderCell>OTTO PO #</TableHeaderCell>
                  <TableHeaderCell>Invoice #</TableHeaderCell>
                  <TableHeaderCell>Product SKU</TableHeaderCell>
                  <TableHeaderCell>Vendor</TableHeaderCell>
                  <TableHeaderCell>OTTO Qty</TableHeaderCell>
                  <TableHeaderCell>Vendor Qty</TableHeaderCell>
                  <TableHeaderCell>OTTO Price</TableHeaderCell>
                  <TableHeaderCell>Vendor Price</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {tableData.length > 0 ? (
                  tableData.map((result, index) => (
                    <TableRow key={index} style={{ 
                      backgroundColor: '#ffebee'
                    }}>
                      <TableCell>{result.dateRange}</TableCell>
                      <TableCell>{result.ottoPO}</TableCell>
                      <TableCell>{result.invoice}</TableCell>
                      <TableCell>{result.productSku}</TableCell>
                      <TableCell>{result.vendorName}</TableCell>
                      <TableCell>{result.ottoQty}</TableCell>
                      <TableCell>
                        {result.ediDataMissing ? (
                          <span style={{ color: '#ff9800' }}>No Vendor Data</span>
                        ) : (
                          result.vendorQty
                        )}
                      </TableCell>
                      <TableCell>{result.ottoPrice}</TableCell>
                      <TableCell>
                        {result.ediDataMissing ? (
                          <span style={{ color: '#ff9800' }}>No Vendor Data</span>
                        ) : (
                          <span style={{ color: result.hasDiscrepancy ? '#d32f2f' : '#4caf50' }}>
                            {result.vendorPrice}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} style={{ textAlign: 'center', padding: '20px' }}>
                      No discrepancies found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </ContentContainer>
      </Modal.Body>
      <Modal.Footer>
        <Modal.FooterElement />
        <Modal.FooterElement>
          <Button primary negative onClick={onCancel} disabled={isSaving}>
            Cancel Processing Job
          </Button>
          <Button onClick={onSaveAndProceed} preloader={isSaving} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save & Proceed'}
          </Button>
        </Modal.FooterElement>
      </Modal.Footer>
    </Modal>
  );
};

export default ProcessingCompleteModal;
